import { env } from '@/lib/env';

function portalHeaders(token: string | null, workspaceId: string): Record<string, string> {
  return { 'Content-Type': 'application/json', 'X-Workspace-Id': workspaceId, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}
function url(path: string): string { return new URL(path, env.apiBaseUrl).toString(); }

export async function requestPortalAccess(workspaceId: string, email: string): Promise<void> {
  await fetch(url('/portal/auth/request-access'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, workspaceId }) });
}
// DEV-ONLY: full URL to the gated dev-login shortcut (skips the email round-trip).
// The backend route 404s in production, so this is harmless to construct anywhere.
export function portalDevLoginUrl(workspaceId: string, email: string): string {
  return url(`/portal/auth/dev-login?email=${encodeURIComponent(email)}&workspaceId=${encodeURIComponent(workspaceId)}`);
}
export async function verifyPortalToken(magicToken: string): Promise<string> {
  const r = await fetch(url('/portal/auth/verify'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: magicToken }) });
  if (!r.ok) throw new Error('verify_failed');
  return (await r.json()).token as string;
}
export async function connectFatSecret(token: string, workspaceId: string): Promise<string> {
  const r = await fetch(url('/portal/integrations/fatsecret/connect'), { method: 'POST', headers: portalHeaders(token, workspaceId), body: '{}' });
  if (!r.ok) throw new Error('connect_failed');
  return (await r.json()).authUrl as string;
}
export interface PortalStat { id: string; attributes: any }
export interface NutritionAnalysis { text: string; generatedAt: string }
export async function fetchPortalNutrition(token: string, workspaceId: string): Promise<{ stats: PortalStat[]; connected?: boolean; analysis?: NutritionAnalysis | null }> {
  const r = await fetch(url('/portal/nutrition'), { headers: portalHeaders(token, workspaceId) });
  if (!r.ok) throw new Error('nutrition_failed');
  return r.json();
}
export async function generateNutritionAnalysis(token: string, workspaceId: string): Promise<NutritionAnalysis> {
  const r = await fetch(url('/portal/nutrition/analysis'), { method: 'POST', headers: portalHeaders(token, workspaceId), body: '{}' });
  if (!r.ok) throw new Error('analysis_failed');
  return r.json();
}
export async function syncPortalFatSecret(token: string, workspaceId: string): Promise<void> {
  const r = await fetch(url('/portal/integrations/fatsecret/sync'), { method: 'POST', headers: portalHeaders(token, workspaceId), body: '{}' });
  if (!r.ok) throw new Error('sync_failed');
}

export interface AboutProfile { summary?: string; age?: number; injuries?: string[]; limitations?: string[]; preferences?: string[]; notes?: string }
export interface ScheduleSlot { weekday: number; time: string }
export interface PortalSession { date: string; note: string; cancelled?: boolean }
export interface PortalMeasurement { date: string; bodyWeight?: number; bodyFat?: number }
export interface PortalOverview {
  client: { name: string };
  trainer: { name: string; photoUrl: string; welcome: string };
  objectives: string;
  program: string;
  aboutText: string;
  aboutProfile: AboutProfile;
  sessions: PortalSession[];
  adjustments: { date: string; note: string }[];
  measurements: PortalMeasurement[];
  nutrition: { dayCount: number; avgCalories: number; avgProtein: number; lastCalories: number };
  inbody: InBody | null;
  schedule: ScheduleSlot[];
  nextSession: { date: string; time?: string } | null;
  thread: PortalMessage[];
}
export interface PortalMessage { from: 'trainer' | 'client'; text: string; at: string }
export interface InBody {
  date?: string;
  bodyFatPct?: number;
  skeletalMuscleMass?: number; // lb
  bmi?: number;
  visceralFat?: number;
  bodyWaterPct?: number;
}
export async function fetchPortalOverview(token: string, workspaceId: string): Promise<PortalOverview> {
  const r = await fetch(url('/portal/overview'), { headers: portalHeaders(token, workspaceId) });
  if (!r.ok) throw new Error('overview_failed');
  return r.json();
}
export async function sendPortalMessage(token: string, workspaceId: string, text: string): Promise<PortalMessage> {
  const r = await fetch(url('/portal/message'), { method: 'POST', headers: portalHeaders(token, workspaceId), body: JSON.stringify({ text }) });
  if (!r.ok) throw new Error('message_failed');
  return r.json();
}
export async function updatePortalObjectives(token: string, workspaceId: string, text: string): Promise<void> {
  const r = await fetch(url('/portal/objectives'), { method: 'PATCH', headers: portalHeaders(token, workspaceId), body: JSON.stringify({ text }) });
  if (!r.ok) throw new Error('objectives_failed');
}
export async function updatePortalAbout(token: string, workspaceId: string, text: string): Promise<{ aboutText: string; aboutProfile: AboutProfile }> {
  const r = await fetch(url('/portal/about'), { method: 'PUT', headers: portalHeaders(token, workspaceId), body: JSON.stringify({ text }) });
  if (!r.ok) throw new Error('about_failed');
  return r.json();
}
